import i18n from 'i18n';
import { FormTemplateFieldMap, FormTemplateValidation } from 'data/template/template.types';
import OperatorValidator from './validators/operatorValidator';
import DateTimeRangeValidator from './validators/dateTimeRangeValidator';
import TriggerValidationOnAnotherFieldValidator from './validators/triggerValidationOnAnotherFieldValidator';
import IPAddressValidator from './validators/IPAddressValidator';
import URLValidator from './validators/URLValidator';
import EmailValidator from './validators/EmailValidator';
import GreaterThanValidator from './validators/GreaterThanValidator';

import Form from './index';
import IntervalValidator from './validators/intervalValidator';
import { DynamicFormFieldMap } from './form.types';
import RegexValidator from './validators/RegexpValidator';
import LengthValidator from './validators/LengthValidator';
import LessThanValidator from './validators/LessThanValidator';

const VALIDATORS = [
  RegexValidator,
  LengthValidator,
  OperatorValidator,
  IPAddressValidator,
  URLValidator,
  DateTimeRangeValidator,
  IntervalValidator,
  TriggerValidationOnAnotherFieldValidator,
  EmailValidator,
  GreaterThanValidator,
  LessThanValidator,
];

function getValidator(type: string) {
  return VALIDATORS.find((validator) => {
    return validator.isType(type);
  });
}

/**
 * This function is used to apply the validators that are defined in the FormTemplate.
 *
 * Validator type 'customFunction'
 *
 * This type should be used when it is required to access dynamic or additional data
 * that is not available in the form itself.  This may be useful when you have multiples
 * forms in the page and a field from a form depends on a field that is available in another
 * form.
 *
 * @return returns an object { isValid: true, errorMessage: '' }
 * isValid is true if field has valid value, otherwise false
 * errorMessage has the corresponding error message when field is invalid.
 */
export function applyFieldValidators(
  validation: FormTemplateValidation | undefined,
  value: any,
  originalValue: any,
  propFields?: FormTemplateFieldMap,
  stateFields?: DynamicFormFieldMap,
  form?: Form,
): { isValid: boolean, errorMessage: string } {
  const validationResult = { isValid: true, errorMessage: '' };

  if (typeof (value) === 'string') value = value.trim();
  if (validation?.required && (!value || value.length === 0)) {
    validationResult.isValid = false;
    validationResult.errorMessage = i18n.t('common.form.fieldError.enterValue');
    return validationResult;
  }

  if (validation?.validators && value) {
    validation.validators.every((validator) => {
      if (!validator.type) return true;

      if (validator.type === 'customFunction') {
        // Check function documentation for detailed explanation
        const { customValidationFunctionMap } = form?.props || {};
        const { customValidationFunction } = validator;

        if ((customValidationFunctionMap == null) ||
            (customValidationFunction == null) ||
            (customValidationFunctionMap[customValidationFunction] == null)) {
          return true;
        }

        const validationFunction = customValidationFunctionMap[customValidationFunction];
        const { isValid, errorMessage } = validationFunction(
          propFields,
          stateFields,
          value,
          originalValue,
          validator,
          form,
        );
        validationResult.isValid = isValid;

        if (validationResult.isValid === false) {
          validationResult.errorMessage = errorMessage || validator.errorMessage || '';
          return false;
        }

      } else {
        const validatorClass = getValidator(validator.type);

        if (validatorClass) {
          validationResult.isValid = validatorClass.isValid(
            propFields,
            stateFields,
            value,
            originalValue,
            validator,
            form,
          );

          if (validationResult.isValid === false) {
            const errorMessage = validatorClass.getErrorMessage(validator);
            validationResult.errorMessage = errorMessage || validator.errorMessage || '';
            return false;
          }
        }
      }

      return true;
    });
  }

  return validationResult;
}

export function checkValid(
  form: Form,
  name: string,
  value: any,
): { isValid: boolean, errorMessage: string } {
  const validationResult = { isValid: true, errorMessage: '' };
  const propFields = form.props.fields; // template fields
  const stateFields = form.state.fields;

  // Check if any avoidRequired field was set
  const avoidRequiredField = stateFields[name].validation?.avoidRequired;
  let skipRequiredValidator = false;
  if (avoidRequiredField) {
    skipRequiredValidator = Object.keys(avoidRequiredField).some((key) => {
      return stateFields[key]?.value === avoidRequiredField[key]
    })
  }
  // skip the fields that do not have validation requirements
  if (!stateFields[name] || !stateFields[name].validation || stateFields[name].hide === 'true' || skipRequiredValidator) return validationResult;
  return applyFieldValidators(stateFields[name].validation, value, stateFields[name].originalValue,
    propFields, stateFields, form);
}
